import { DesignScheme } from '@data/buildingProjects/DesignSchemes/designScheme';
import FVector from '@data/geometry/FVector.js';
import { GeometryUtils } from '@data/geometry/GeometryUtils';
import CloseToDesignSchemesBlock from '@data/Simulations/pipeline/blocks/CloseToDesignSchemesBlock';
import ShadowBlock from '@data/Simulations/pipeline/blocks/ShadowBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { LayerKind, SchemeKey } from '@visualizer/types';
import { SensorData } from 'Simulations/SensorPoint';
import * as THREE from 'three';
import Grid from '../../Geodata/grid';
import { FloorBasedRenderer } from './FloorBased/FloorBasedRenderer';

export function removeDuplicatePoints(floor: FVector[]) {
    const uniquePos = [];
    const seenPos = new Set();

    if (!Array.isArray(floor)) {
        floor = [floor];
    }

    for (const point of floor) {
        if (point == undefined) continue;

        const key = `${point.x},${point.y},${point.z}`;

        if (!seenPos.has(key)) {
            seenPos.add(key);
            uniquePos.push(point);
        }
    }

    return uniquePos;
}

export function generatePointsCloud(sensorDatas: SensorData[]) {
    let pointsCloud: FVector[] = [];
    const halfSide = Grid.gridRes / 2;

    sensorDatas.forEach((sensorData) => {
        if (!sensorData || sensorData.getScalarValue() == 0) return;

        const { x, y, z } = sensorData.getPosition();
        const positions = [
            new FVector(x + halfSide, y + halfSide, z - halfSide),
            new FVector(x + halfSide, y - halfSide, z - halfSide),
            new FVector(x - halfSide, y + halfSide, z - halfSide),
            new FVector(x - halfSide, y - halfSide, z - halfSide),
            new FVector(x + halfSide, y + halfSide, z + halfSide),
            new FVector(x + halfSide, y - halfSide, z + halfSide),
            new FVector(x - halfSide, y + halfSide, z + halfSide),
            new FVector(x - halfSide, y - halfSide, z + halfSide),
        ];

        pointsCloud.push(...positions);
    });

    return pointsCloud;
}

/**
 * Remove duplicate points keeping only the one with the highest Z
 */
export function removeDuplicatePointsWithHighestZ(points: FVector[]): FVector[] {
    const uniquePointsMap = new Map<string, FVector>();

    for (const point of points) {
        const key = `${point.x},${point.y}`;
        const existing = uniquePointsMap.get(key);

        if (!existing || point.z > existing.z) {
            uniquePointsMap.set(key, point);
        }
    }

    return Array.from(uniquePointsMap.values());
}

/**
 * Extract perimeter vertices from a BufferGeometry
 */
export function getPerimeterPoints(geometry: THREE.BufferGeometry): FVector[] {
    const positionArray = geometry.attributes.position.array as ArrayLike<number>;
    const vertices: FVector[] = [];

    for (let i = 0; i < positionArray.length; i += 3) {
        const x = positionArray[i];
        const y = positionArray[i + 1];
        const z = positionArray[i + 2];

        vertices.push(new FVector(x, y, z));
    }

    return vertices;
}

/**
 * Compute centroid of a set of points
 */
export function calculateCentroid(points: FVector[]): FVector {
    const centroid = new FVector(0, 0, 0);

    for (const point of points) {
        centroid.add(point);
    }

    centroid.div(points.length || 1);
    return centroid;
}

export function sortPointsCCW(points: FVector[], centroid: FVector): FVector[] {
    return points.slice().sort((a, b) => {
        const angA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
        const angB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
        return angA - angB;
    });
}

// =====================================
// COMPONENTS UTILS
// =====================================

export function computeOutwardOffsetPointsForEdge(
    perimeter: FVector[],
    a: FVector,
    b: FVector,
    depth: number
): [THREE.Vector2, THREE.Vector2] {
    const edge = new THREE.Vector2(b.x - a.x, b.y - a.y);
    if (edge.lengthSq() <= 1e-8) {
        return [new THREE.Vector2(a.x, a.y), new THREE.Vector2(b.x, b.y)];
    }

    edge.normalize();
    const leftNormal = new THREE.Vector2(-edge.y, edge.x);
    const outward = GeometryUtils.polygonIsCCW(perimeter.map((p) => new THREE.Vector2(p.x, p.y)))
        ? leftNormal.clone().multiplyScalar(-1)
        : leftNormal;
    const offset = outward.multiplyScalar(depth);

    return [new THREE.Vector2(a.x + offset.x, a.y + offset.y), new THREE.Vector2(b.x + offset.x, b.y + offset.y)];
}

/** Collect mesh groups used by shadow/keepout raycasts. */
export function getShadowMeshesForSet(setId: string): THREE.Group[] {
    const designSchemeController = ControllerManager.getInstance().getDesignSchemeController();

    const schemes = designSchemeController.getDesignSchemes(setId) ?? [];

    return schemes
        .filter((scheme) => scheme?.getRenderer()?.getMeshGroup() && scheme.getRenderer().getVisibility())
        .map((scheme) => {
            const meshGroup = scheme?.getRenderer()?.getMeshGroup();
            if (!meshGroup) {
                return new THREE.Group();
            }

            const copy =
                scheme.getRendererKey() === SchemeKey.FLOOR_BASED
                    ? cloneWithoutFloorBasedHorizontalSurfaces(meshGroup)
                    : meshGroup.clone(true);

            prepareMeshGroupForRaycast(copy);
            return copy;
        });
}

/** Meshes used for close-to-DS distance and inside/outside checks. */
export function getCloseToDesignSchemesMeshesForSet(setId: string): THREE.Group[] {
    const designSchemeController = ControllerManager.getInstance().getDesignSchemeController();
    const schemes = designSchemeController.getDesignSchemes(setId) ?? [];

    return schemes
        .filter((scheme) => scheme?.getRenderer()?.getMeshGroup() && scheme.getRenderer().getVisibility())
        .map((scheme) => {
            const renderer = scheme.getRenderer();
            if (!renderer) {
                return new THREE.Group();
            }

            // Return only the shell for FloorBased, no components:
            if (scheme.getRendererKey() === SchemeKey.FLOOR_BASED) {
                const floorBasedRenderer = renderer as FloorBasedRenderer;
                const shellGroup = floorBasedRenderer.buildFacadeShellGroup(scheme.getSensorDatasFor(LayerKind.MAIN));
                prepareMeshGroupForRaycast(shellGroup);
                return shellGroup;
            }

            const meshGroup = renderer.getMeshGroup().clone(true);
            prepareMeshGroupForRaycast(meshGroup);
            return meshGroup;
        });
}

function prepareMeshGroupForRaycast(group: THREE.Group): void {
    group.updateMatrixWorld(true);

    group.traverse((child) => {
        const c = child as THREE.Mesh;
        if (c.isMesh && c.material) {
            (c.material as THREE.Material).side = THREE.DoubleSide;
        }
    });
}

function cloneWithoutFloorBasedHorizontalSurfaces(root: THREE.Group): THREE.Group {
    const cloneNode = <T extends THREE.Object3D>(node: T): T | null => {
        if (node.name === 'Ground' || node.name === 'Ceiling') {
            return null;
        }

        const cloned = node.clone(false) as T;
        node.children.forEach((child) => {
            const clonedChild = cloneNode(child);
            if (clonedChild) {
                cloned.add(clonedChild);
            }
        });

        return cloned;
    };

    return cloneNode(root) ?? new THREE.Group();
}

export interface RaycastMeshData {
    mesh: THREE.Mesh;
    box: THREE.Box3;
}

export function buildRaycastMeshData(groups: THREE.Group[]): RaycastMeshData[] {
    const result: RaycastMeshData[] = [];

    for (const group of groups) {
        group.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (!mesh.isMesh) return;

            const box = new THREE.Box3().setFromObject(mesh);

            result.push({ mesh, box });
        });
    }

    return result;
}

export function buildGlobalAABB(raycastMeshes: RaycastMeshData[]): THREE.Box3 {
    const box = new THREE.Box3();
    for (const { box: meshBox } of raycastMeshes) {
        box.union(meshBox);
    }
    return box;
}

export function rebuildBVHForGroups(groups: THREE.Group[]) {
    let meshCount = 0;
    let bvhCount = 0;

    for (const group of groups) {
        group.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (!mesh.isMesh) return;

            meshCount++;

            const geom: any = mesh.geometry;
            if (!geom?.computeBoundsTree) return;

            geom.disposeBoundsTree?.();
            geom.computeBoundsTree();

            if (geom.boundsTree) bvhCount++;
        });
    }
}

export function buildShadowBlockForSet(designSchemes: DesignScheme[], meshGroups: THREE.Group[], inverted: boolean = false): ShadowBlock {
    // 1) Collect DesignScheme polygons:
    const polygons = [];
    for (const ds of designSchemes) {
        polygons.push(ds.getFootprint());
    }

    // 2) Build BVH + raycast data
    rebuildBVHForGroups(meshGroups);
    const raycastMeshes = buildRaycastMeshData(meshGroups);
    const globalBox = buildGlobalAABB(raycastMeshes);

    return new ShadowBlock({
        polygons: polygons,
        isInverted: inverted,
        raycastMeshes,
        globalBox,
    });
}

export function buildCloseToDesignSchemesBlockForSet(
    _designSchemes: DesignScheme[],
    meshGroups: THREE.Group[],
    maxDistance: number
): CloseToDesignSchemesBlock {
    rebuildBVHForGroups(meshGroups);

    const raycastMeshes = buildRaycastMeshData(meshGroups);
    const globalBox = buildGlobalAABB(raycastMeshes).expandByScalar(Math.max(0, maxDistance));

    return new CloseToDesignSchemesBlock({
        raycastMeshes,
        globalBox,
        maxDistance,
    });
}
